import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, type Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { OvertimeDialog } from './OvertimeDialog';
import { scopeOf } from './SettlementCard';
import { eventSettlementsKey, hoursLabel, multiplierLabel, OVERTIME_RESOURCE, SETTLEMENTS_KEY, SETTLEMENTS_RESOURCE, STATES, type Overtime, type OvertimeList, type Settlement } from './types';
import './settlements.css';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

/** The event's people and their settlements (who may see settlements): state, total, a link to each card. */
function PeopleSettlements({ eventId, me }: { eventId: string; me: Me }) {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: eventSettlementsKey(eventId), queryFn: () => api<{ settlements: Settlement[] }>(`/api/settlements/events/${eventId}`) });
  const prepare = useMutation({
    mutationFn: () => api<{ created: number; updated: number }>(`/api/settlements/events/${eventId}/prepare`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY }),
  });
  const rows = list.data?.settlements ?? [];
  return (
    <section className="fin-section" aria-label="Rozliczenia osób" data-feature-id="settlements.card">
      <div className="stl-section-head">
        <h3>Rozliczenia osób</h3>
        {scopeOf(me, 'edit') === 'all' && (
          <button className="btn" type="button" disabled={prepare.isPending} onClick={() => prepare.mutate()}>
            Przygotuj rozliczenia
          </button>
        )}
      </div>
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać rozliczeń')}</p>}
      {prepare.error && <p className="alert" role="alert">{errorText(prepare.error, 'Nie udało się przygotować rozliczeń')}</p>}
      {list.data && rows.length === 0 && <p className="muted">Rozliczeń jeszcze nie ma. Powstają same dzień po wydarzeniu (6:00) dla każdego z obsady, kto nie odmówił.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table">
            <thead>
              <tr>
                <th scope="col">Osoba</th>
                <th scope="col">Stan</th>
                <th scope="col" className="num">
                  Dniówka
                </th>
                <th scope="col" className="num">
                  Razem
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const day = s.items.find((i) => i.kind === 'dniowka');
                return (
                  <tr key={s.id}>
                    <td>
                      <Link to={`/finanse/rozliczenia/${s.id}`} aria-label={`Rozliczenie: ${s.personName}`}>
                        {s.personName}
                      </Link>
                    </td>
                    <td>{STATES[s.state]}</td>
                    <td className="num">{day?.rateGrosze === undefined ? '—' : day.description === 'brak stawki' ? <span className="fin-warn">brak stawki</span> : formatPln(day.rateGrosze)}</td>
                    <td className="num">{s.totalGrosze === undefined ? '—' : formatPln(s.totalGrosze)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Overtime of the event: hours and multiplier only (the event's manager sees no amounts, D16). */
function OvertimeSection({ eventId, me }: { eventId: string; me: Me }) {
  const queryClient = useQueryClient();
  const key = [...SETTLEMENTS_KEY, 'overtime', eventId] as const;
  const list = useQuery({ queryKey: key, queryFn: () => api<OvertimeList>(`/api/settlements/events/${eventId}/overtime`) });
  const [dialog, setDialog] = useState<{ editing?: Overtime } | null>(null);
  const [error, setError] = useState('');
  const editable = can(me, OVERTIME_RESOURCE, 'edit');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY });
  async function remove(o: Overtime) {
    try {
      await api(`/api/settlements/overtime/${o.id}?version=${o.version}`, { method: 'DELETE' });
      setError('');
    } catch (e) {
      setError(errorText(e, 'Nie udało się usunąć'));
    }
    refresh();
  }
  const data = list.data;
  return (
    <section className="fin-section" aria-label="Nadgodziny" data-feature-id="settlements.overtime">
      <div className="stl-section-head">
        <h3>Nadgodziny</h3>
        {editable && data && data.people.length > 0 && (
          <button className="btn" type="button" onClick={() => setDialog({})}>
            Dodaj nadgodziny
          </button>
        )}
      </div>
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać nadgodzin')}</p>}
      {error && <p className="alert" role="alert">{error}</p>}
      {data && data.overtime.length === 0 && <p className="muted">{data.people.length === 0 ? 'Brak obsady: nadgodziny wpisuje się osobom z obsady.' : 'Brak nadgodzin.'}</p>}
      {data && data.overtime.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table">
            <thead>
              <tr>
                <th scope="col">Osoba</th>
                <th scope="col">Nadgodziny</th>
                <th scope="col">Opis</th>
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.overtime.map((o) => (
                <tr key={o.id}>
                  <td>{o.personName}</td>
                  <td>
                    {hoursLabel(o.hoursMilli)} h × {multiplierLabel(o.multiplierBp)}
                  </td>
                  <td>{o.description}</td>
                  <td className="q-actions">
                    {editable && (
                      <>
                        <button className="btn ghost small" type="button" onClick={() => setDialog({ editing: o })}>
                          Zmień
                        </button>
                        <button className="btn icon ghost" type="button" aria-label={`Usuń nadgodziny: ${o.personName}`} title="Usuń" onClick={() => void remove(o)}>
                          ×
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog && data && (
        <OvertimeDialog
          eventId={eventId}
          people={data.people}
          editing={dialog.editing}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

/**
 * "Rozliczenia" on the event card (F39): the people's settlements for whoever may see them, and the overtime for
 * whoever may type it (the event's manager: overtime only, without amounts).
 */
export function EventSettlementsTab({ eventId, me }: { eventId: string; me: Me }) {
  return (
    <div role="tabpanel" aria-label="Rozliczenia" className="stl-tab">
      {can(me, SETTLEMENTS_RESOURCE, 'view') && <PeopleSettlements eventId={eventId} me={me} />}
      {can(me, OVERTIME_RESOURCE, 'view') && <OvertimeSection eventId={eventId} me={me} />}
    </div>
  );
}
